import React, { useState } from 'react';
import { Box, Typography, Menu, MenuItem, Snackbar, Alert } from '@mui/material';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';

const StatusDropdownPill = ({ 
  currentStatus, 
  onStatusChange, 
  disabled = false,
  sx = {} 
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const open = Boolean(anchorEl);

  const statusConfig = {
    OPEN: { label: 'Ouvert', color: '#027BC6', backendValue: 'ouvert' },
    IN_PROGRESS: { label: 'En cours', color: '#FEA26F', backendValue: 'en_cours_de_verification' },
    VERIFIED: { label: 'Vérifié', color: '#0AD69F', backendValue: 'verifie' }
  };

  // Map backend enum names to backend values
  const getBackendValue = (enumName) => {
    return statusConfig[enumName]?.backendValue || enumName;
  };

  const handleClick = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (newStatus) => {
    if (newStatus !== currentStatus) {
      // Business logic: Cannot go back to OPEN once changed
      if (currentStatus !== 'OPEN' && newStatus === 'OPEN') {
        setSnackbar({ open: true, message: 'Impossible de revenir au statut "Ouvert" une fois la vérification commencée', severity: 'warning' });
        handleClose();
        return;
      }
      
      // Show confirmation dialog
      setPendingStatus(newStatus);
      setShowConfirmation(true);
    }
    handleClose();
  };

  const handleConfirmStatusChange = () => {
    if (pendingStatus) {
      const backendValue = getBackendValue(pendingStatus);
      onStatusChange(backendValue);
    }
    setShowConfirmation(false);
    setPendingStatus(null);
  };

  const handleCancelStatusChange = () => {
    setShowConfirmation(false);
    setPendingStatus(null);
  };

  const config = statusConfig[currentStatus] || { label: currentStatus, color: '#6B7280' };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', ...sx }}>
      <Box
        onClick={handleClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '26px',
          borderRadius: '30px',
          padding: '0 12px',
          backgroundColor: config.color,
          border: `1px solid ${config.color}`,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          '&:hover': disabled ? {} : { opacity: 0.9 }
        }}
      >
        <Typography
          sx={{
            color: '#FFFFFF',
            textAlign: 'center',
            font: 'normal normal 600 14px/18px Montserrat',
            letterSpacing: '0px',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            marginRight: '6px'
          }}
        >
          {config.label}
        </Typography>
        <img 
          src={chevronDownIcon} 
          alt="dropdown"
          style={{ 
            width: '12px', 
            height: '12px',
            filter: 'brightness(0) invert(1)' // Make it white
          }} 
        />
      </Box>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #E3E3E3'
          }
        }}
      >
        {Object.entries(statusConfig).map(([statusKey, statusValue]) => (
          <MenuItem
            key={statusKey}
            onClick={() => handleMenuItemClick(statusKey)}
            disabled={statusKey === currentStatus}
            sx={{
              backgroundColor: statusKey === currentStatus ? statusValue.color : 'transparent',
              color: statusKey === currentStatus ? '#FFFFFF' : '#374151',
              '&:hover': {
                backgroundColor: statusValue.color,
                color: '#FFFFFF',
              },
              font: 'normal normal 600 14px/18px Montserrat',
              textTransform: 'none',
              minWidth: '120px',
              borderRadius: '4px',
              margin: '2px 4px'
            }}
          >
            {statusValue.label}
          </MenuItem>
        ))}
      </Menu>
      
      <ConfirmationDialog
        open={showConfirmation}
        onClose={handleCancelStatusChange}
        onConfirm={handleConfirmStatusChange}
        type="warning"
        title="Confirmer le changement de statut"
        message={
          <Typography sx={{ fontSize: '16px', textAlign: 'center' }}>
            Êtes-vous sûr de vouloir changer le statut de{' '}
            <span style={{ color: '#007BC6', fontWeight: 'bold' }}>
              {config.label}
            </span>{' '}
            vers{' '}
            <span style={{ color: '#007BC6', fontWeight: 'bold' }}>
              {pendingStatus ? statusConfig[pendingStatus]?.label : ''}
            </span>
            ?
          </Typography>
        }
        confirmText="Confirmer"
        cancelText="Annuler"
      />
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StatusDropdownPill;
